<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import WebAppIframe from './components/WebAppIframe.vue';
import AppChrome from './components/nav/AppChrome.vue';
import AppSplash from './components/app/AppSplash.vue';
import EcosShelfProgress from '@/components/common/EcosShelfProgress.vue';
import { cancelLegacyWaits, legacyLoader, legacyLoaderDone } from '@/iframe/legacyLoader';
import { updateServiceWorker } from '@/utils/app/registerServiceWorker';
import { useTranslator } from '@/composables/useTranslator';
import { eventBus } from '@/utils/core/eventBus';
import { useAppChrome } from '@/composables/useAppChrome';
import { session } from '@/utils/auth/sessionService';
const t = useTranslator();
const route = useRoute();
const isLegacyRoute = computed(() => Boolean(route.meta?.legacyView));
const { visible: chromeVisible, desktop, nav } = useAppChrome();
const hasTopBar = computed(() => chromeVisible.value && desktop.value);
const iframeVisible = computed(() => isLegacyRoute.value && nav.page === 'app');
const showSplash = computed(() => session.status === 'pending' || !route.matched.length || (isLegacyRoute.value && !iframeVisible.value));
const legacyLoaderSync = computed(() => legacyLoader.mode === 'block' && legacyLoader.value !== null);
const legacyLoaderLabel = computed(() => (legacyLoaderSync.value ? `${t('Synchronization')}…` : t('Loading')));
const showUpdateSnackbar = ref(false);
let pendingUpdate = false;
let externisReady = false;

function onUpdateAvailable() {
	if (externisReady) {
	} else {
		pendingUpdate = true;
	}
}

function onExternisReady() {
	externisReady = true;
	if (pendingUpdate) {
		pendingUpdate = false;
	}
}

function confirmUpdate() {
	showUpdateSnackbar.value = false;
	updateServiceWorker();
}

onMounted(() => {
	window.addEventListener('sw-update-available', onUpdateAvailable);
	eventBus.on('externis-ready', onExternisReady);
});

onUnmounted(() => {
	window.removeEventListener('sw-update-available', onUpdateAvailable);
	eventBus.off('externis-ready', onExternisReady);
});
</script>

<template>
	<v-app class="app-shell">
		<AppChrome />
		<v-main class="app-shell__main">
			<WebAppIframe :visible="iframeVisible" :safe-top="!hasTopBar" />
			<router-view />
			<AppSplash v-if="showSplash" />
		</v-main>
		<v-fade-transition>
			<EcosShelfProgress v-if="legacyLoader.visible && iframeVisible" overlay :value="legacyLoader.value" :theme="legacyLoader.theme ?? ''" :label="legacyLoaderLabel" :done-label="legacyLoaderSync ? t('Synchronized') : ''" @done="legacyLoaderDone">
				<template v-if="legacyLoader.cancellable" #actions>
					<v-btn variant="tonal" rounded="xl" color="primary" prepend-icon="close" @click="cancelLegacyWaits">{{ t('Cancel') }}</v-btn>
				</template>
			</EcosShelfProgress>
		</v-fade-transition>
		<v-snackbar v-model="showUpdateSnackbar" :timeout="-1" location="bottom" rounded="xl">
			{{ t('New version available. Please refresh the page.') }}
			<template #actions>
				<v-btn variant="text" @click="showUpdateSnackbar = false">{{ t('Close') }}</v-btn>
				<v-btn color="primary" variant="tonal" @click="confirmUpdate">{{ t('Update') }}</v-btn>
			</template>
		</v-snackbar>
	</v-app>
</template>
